import React, { Suspense, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import { appPath, mergeSentryConfig, sentryDSN } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import { essentialLocales, i18n, setAppLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { AppLayout } from '@fs/zion-ui'
import * as Sentry from '@sentry/browser'
import App from './App'
import { initQualtricsLanguage } from './lib/qualtricsLanguages'
import './locales'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      beforeSend(event) {
        const exception = event?.exception?.values?.[0]
        // const message = exception?.value || '';
        const stacktrace = exception?.stacktrace?.frames || []
        // Check if the stack trace includes a third-party URL

        const isThirdPartyErrorInStack = stacktrace.some((frame) => frame.filename?.includes('consent.trustarc.com'))

        const isThirdPartyErrorInBreadcrumbs = event?.breadcrumbs?.some(
          (breadcrumb) =>
            breadcrumb.category === 'http' &&
            typeof breadcrumb.data?.url === 'string' &&
            breadcrumb.data.url.includes('consent.trustarc.com')
        )

        if (isThirdPartyErrorInStack || isThirdPartyErrorInBreadcrumbs) {
          // Drop the event
          return null
        }
        return event
      },
      ignoreErrors: [
        // Browser extensions (chrome-extension, moz-extension, etc.)
        /-extension:\//,
        // Chrome Extension error that is not caught by the first
        /Extension context invalidated/,
        // Non-critical localstorage errors
        /QuotaExceededError/,
        // Non-critical localstorage errors
        /StorageQuotaExceeded/,
        // Happens when a user session times out
        'Request failed with status code 401',
        // Happens in WebKit, particularly iOS Mail. Unresolved WebKit issue: https://bugs.webkit.org/show_bug.cgi?id=197050
        /UnknownError.*Indexed Database server.*/,
      ],
      denyUrls: [
        // Error caused by GDPR cookie consent management partner Trustarc
        /consent\.trustarc\.com/,
        // Error caused by qualtrics (ex. Loading chunk 10 failed...)
        /https?:\/\/.*siteintercept\.qualtrics\.com/,
      ],
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here as flags, languages and translations are now configured and available
function FrontierWrapper() {
  const [hideFSHF, setHideFSHF] = useState(false)
  setAppLocales(essentialLocales)

  return (
    <Router basename={base}>
      <Root analytics footer={{ hidden: hideFSHF }} header={{ hidden: hideFSHF }} helpChatbot={false}>
        <AppLayout fullWidth>
          <App hideFSHF={hideFSHF} setHideFSHF={setHideFSHF} />
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
function FrontierRoot() {
  // Add Qualtrics content to the app
  useEffect(() => {
    // Qualtrics Script code
    /* eslint-disable */
    // prettier-ignore
    ;(function(){var g=function(e,h,f,g){
    this.get=function(a){for(var a=a+"=",c=document.cookie.split(";"),b=0,e=c.length;b<e;b++){for(var d=c[b];" "==d.charAt(0);)d=d.substring(1,d.length);if(0==d.indexOf(a))return d.substring(a.length,d.length)}return null};
    this.set=function(a,c){var b="",b=new Date;b.setTime(b.getTime()+6048E5);b="; expires="+b.toGMTString();document.cookie=a+"="+c+b+"; path=/; "};
    this.check=function(){var a=this.get(f);if(a)a=a.split(":");else if(100!=e)"v"==h&&(e=Math.random()>=e/100?0:100),a=[h,e,0],this.set(f,a.join(":"));else return!0;var c=a[1];if(100==c)return!0;switch(a[0]){case "v":return!1;case "r":return c=a[2]%Math.floor(100/c),a[2]++,this.set(f,a.join(":")),!c}return!0};
    this.go=function(){if(this.check()){var a=document.createElement("script");a.type="text/javascript";a.src=g;document.body&&document.body.appendChild(a)}};
    this.start=function(){var t=this;"complete"!==document.readyState?window.addEventListener?window.addEventListener("load",function(){t.go()},!1):window.attachEvent&&window.attachEvent("onload",function(){t.go()}):t.go()};};
    try{(new g(100,"r","QSI_S_ZN_cI4OucMtvbHUxSZ","https://znci4oucmtvbhuxsz-lds.siteintercept.qualtrics.com/SIE/?Q_ZID=ZN_cI4OucMtvbHUxSZ")).start()}catch(i){}})();
    /* eslint-enable */
  }, [])

  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
      <div id="ZN_cI4OucMtvbHUxSZ" />
    </React.StrictMode>
  )
}

// Must run before the SIE script (added in FrontierRoot above) can evaluate window.qualtricsLanguage,
// so it runs at module scope rather than in an effect.
initQualtricsLanguage(i18n)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))
